import {
  ChangeEvent,
  MutableRefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';

import { Category, SearchSuggestion } from '@/__generated_graphql__/graphql';
import { NoSearchResult } from '@/components/NoSearchResult/NoSearchResult';
import { ParticipantTabs } from '@/components/ParticipantTabs/ParticipantTabs';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import { Button } from '@/components/ui/Button/Button';
import { ClickableChip } from '@/components/ui/ClickableChip/ClickableChip';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import {
  MAX_VISIBLE_DEFAULT_CATEGORIES_SUGGESTIONS,
  MAX_VISIBLE_DEFAULT_SUGGESTIONS
} from '@/constants/common';

import * as S from './SearchBar.styles';

type Props = {
  isOpen: boolean;
  query?: string | Category;
  searchSuggestion: SearchSuggestion[];
  elRef: MutableRefObject<HTMLDivElement | null>;
  onClose: () => void;
  onSuggestion?: (suggestion: SearchSuggestion) => void;
  onCategory?: (category: Category) => void;
  onClear: () => void;
  onChange: (e: ChangeEvent<HTMLInputElement> | string) => void;
  onKeyDown: (e: HTMLInputElement | null) => void;
  isModal: boolean;
  categories: Category[];
};

export const SearchBarDropdown = ({
  onClear,
  onClose,
  onSuggestion,
  onCategory,
  onChange,
  onKeyDown,
  searchSuggestion,
  categories,
  isOpen,
  query = '',
  elRef,
  isModal
}: Props) => {
  const { t } = useTranslation();
  const [isFocusedInput, setIsFocusedInput] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isModal || !inputRef.current || !elRef.current) return;
    const blurInput = () => inputRef.current?.blur();
    elRef.current.addEventListener('scroll', blurInput);
    window.addEventListener('scroll', blurInput);

    return () => {
      window.removeEventListener('scroll', blurInput);
      elRef.current?.removeEventListener('scroll', blurInput);
    };
  }, [elRef.current, inputRef.current, isModal]);

  const defaultSuggestions = searchSuggestion?.slice(
    0,
    MAX_VISIBLE_DEFAULT_SUGGESTIONS
  );

  const defaultCategories = categories?.slice(
    0,
    MAX_VISIBLE_DEFAULT_CATEGORIES_SUGGESTIONS
  );

  const computedValue = typeof query === 'object' ? query.name : query;

  const searchInputProps = {
    inputRef,
    isFullWidth: true,
    isBordered: true,
    query: computedValue,
    isOpen: isFocusedInput,
    placeholder: t('SearchBar.placeholder'),
    onFocus: () => setIsFocusedInput(true),
    onBlur: () => setIsFocusedInput(false),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onKeyDown(inputRef.current);
    },
    onChange,
    onReset: onClear
  };

  return (
    <S.Dropdown
      $isOpen={isOpen}
      $isModal={isModal}
      $hasValue={!!query}
      ref={elRef}
    >
      {isModal && (
        <S.DropdownHeader>
          <S.DropdownHeaderSection>
            <SearchInput {...searchInputProps} />
            <Button onClick={onClose} variant="link" size="m">
              <Typography variant="link_15">
                {t('SearchBar.cancel_button_label')}
              </Typography>
            </Button>
          </S.DropdownHeaderSection>
          {!(typeof query === 'string' && query) && <ParticipantTabs />}
        </S.DropdownHeader>
      )}
      {isOpen && (
        <S.DropdownContent>
          {typeof query === 'string' && query ? (
            <S.DropdownItemsWrapper>
              {searchSuggestion.length ? (
                searchSuggestion?.map(suggestion => {
                  const regex = new RegExp(`(${query})`, 'gi');
                  const parts = suggestion.searchTerm.split(regex);

                  return (
                    <S.DropdownItem
                      data-testid="suggestion"
                      key={suggestion.id}
                      onClick={() => onSuggestion?.(suggestion)}
                    >
                      {parts.map((part, index) => {
                        const equalSymbol =
                          part.toLowerCase() === computedValue.toLowerCase();

                        return (
                          <Typography
                            style={{
                              display: 'inline-block',
                              fontWeight: equalSymbol ? '600' : 'normal'
                            }}
                            variant="body1"
                            key={index}
                          >
                            {part}
                          </Typography>
                        );
                      })}
                    </S.DropdownItem>
                  );
                })
              ) : (
                <NoSearchResult />
              )}
            </S.DropdownItemsWrapper>
          ) : (
            <>
              <S.SuggestionsSectionWrapper>
                <S.DropdownSectionTitle>
                  {t('SearchBar.suggestions_section')}
                </S.DropdownSectionTitle>
                <S.DropdownItemsWrapper>
                  {defaultSuggestions?.map(suggestion => (
                    <S.DropdownItem
                      key={suggestion.id}
                      data-testid="popular-suggestion"
                      onClick={() => onSuggestion?.(suggestion)}
                    >
                      <Typography variant="body1">
                        {suggestion.searchTerm}
                      </Typography>
                    </S.DropdownItem>
                  ))}
                </S.DropdownItemsWrapper>
              </S.SuggestionsSectionWrapper>
              <S.CategoriesSectionWrapper>
                <S.DropdownSectionTitle>
                  {t('SearchBar.categories_section')}
                </S.DropdownSectionTitle>
                <S.ChipsContainer>
                  {defaultCategories?.map((category: Category) => (
                    <ClickableChip
                      id={category.id}
                      key={category.id}
                      data-testid="clickable-chip"
                      label={category.name}
                      onClick={() => onCategory?.(category)}
                    />
                  ))}
                </S.ChipsContainer>
              </S.CategoriesSectionWrapper>
            </>
          )}
        </S.DropdownContent>
      )}
    </S.Dropdown>
  );
};
